
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

interface Feature {
  id: string;
  name: string;
  description: string | null;
}

export const UserFeatures = ({ userId }: { userId: string }) => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserFeatures = async () => {
      try {
        // Get all features
        const { data: allFeatures, error: featuresError } = await supabase
          .from("features")
          .select("*");

        if (featuresError) {
          throw featuresError;
        }

        // For each feature, check if user has access
        const userFeatures = await Promise.all(
          allFeatures.map(async (feature) => {
            const { data, error } = await supabase.rpc("has_feature_access", {
              user_id: userId,
              feature_name: feature.name,
            });

            if (error) {
              console.error(`Error checking access for ${feature.name}:`, error);
              return null;
            }

            return data ? feature : null;
          })
        );

        setFeatures(userFeatures.filter(Boolean) as Feature[]);
      } catch (err) {
        console.error("Error fetching user features:", err);
        toast({
          title: "Error",
          description: "Could not fetch feature access. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserFeatures();
    }
  }, [userId, toast]);

  if (loading) {
    return <div className="text-sm text-slate-500">Loading features...</div>;
  }

  if (features.length === 0) {
    return (
      <div className="text-sm text-slate-500">
        No features available for this user.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-md font-semibold text-slate-700">Available Features</h3>
      <div className="flex flex-wrap gap-2">
        {features.map((feature) => (
          <Badge key={feature.id} variant="outline" className="capitalize">
            {feature.name}
          </Badge>
        ))}
      </div>
      <div className="mt-2">
        {features.map((feature) => (
          <div key={feature.id} className="mt-2 text-sm">
            <span className="font-medium">{feature.name}:</span>{" "}
            <span className="text-slate-600">{feature.description || "No description available"}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
